import { useState, useMemo, useEffect } from 'react';
import diacritics from 'diacritics';
import { WordlistContextValue, WordlistTab } from '../_contexts/WordlistContext';
import stripHtml from '../../../../utils/wordlist/stripHtml';

const adjustWordForSearch = (word: string): string => {
  const cleanedUpWord = diacritics.remove(word).replace(/[\W_]/gm, ' '); // replace no characters with spaces
  // Regex on word boundaries
  // or at least 2 characters with a space
  // or at least 1 character without space to capture beginning of words
  return (cleanedUpWord.match(/[^\W_]{2,} |[^\W_]{1,}$/gm) || []).join('');
};

const findSearchTermInWord = (word: string, sanitizedSearchTerm: string) => {
  const searchExp = new RegExp(`${sanitizedSearchTerm}`, 'gmi');
  const result = adjustWordForSearch(stripHtml(word, false).trim());

  // uses a regular expression to find a term in a specific word (after using the same method for cleaning up)
  const isFound = searchExp.test(result);
  // After searching the index needs to be reset to 0 to be able to use the same regular expression for the next search
  searchExp.lastIndex = 0;

  return isFound;
};

export default function useWordlistSearch({
  switched,
  wordlist,
  setSelectedWord,
  currentTab,
}: Pick<WordlistContextValue, 'switched' | 'wordlist' | 'setSelectedWord' | 'currentTab'>) {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const sanitizedSearchTerm = useMemo<string>(() => adjustWordForSearch(searchTerm), [searchTerm]);

  const searchResults = useMemo(() => {
    if (!wordlist || !sanitizedSearchTerm || sanitizedSearchTerm.length < 2) {
      return [];
    }

    const member = switched ? 'translated' : 'untranslated';
    return wordlist.words.filter(w => findSearchTermInWord(w[member], sanitizedSearchTerm));
  }, [sanitizedSearchTerm, switched, wordlist]);

  const searchError = useMemo(() => {
    if (sanitizedSearchTerm.length < 2) {
      return 'MODULES.MATERIAL.COMPONENTS.WORD_LISTS.SEARCH_INSUFFICIENT_CHAR';
    }

    if (searchResults?.length === 0) {
      return 'MODULES.MATERIAL.COMPONENTS.WORD_LISTS.SEARCH_NOT_FOUND';
    }

    return undefined;
  }, [sanitizedSearchTerm, searchResults]);

  useEffect(() => {
    if (currentTab === WordlistTab.SEARCH) {
      if (!sanitizedSearchTerm || sanitizedSearchTerm.length < 2) {
        setSelectedWord(undefined);
      }
    }
  }, [currentTab, sanitizedSearchTerm, setSelectedWord]);

  useEffect(() => {
    if (currentTab === WordlistTab.SEARCH) {
      if (searchResults.length) {
        setSelectedWord(searchResults[0]);
      } else {
        setSelectedWord(undefined);
      }
    }
  }, [currentTab, searchResults, setSelectedWord]);

  useEffect(() => {
    setSearchTerm('');
  }, [currentTab, setSearchTerm, switched]);

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    searchError,
  };
}
